import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { api } from './client'
import type { AgentLog, AgentLogsPage, AgentPin } from '../types'

export type { AgentLog }

export interface AgentLogsFilter {
  level?: string
  since?: string
  limit?: number
}

export interface AgentLogsOptions {
  enabled?: boolean
  refetchInterval?: number | false
}

/**
 * Per-phone agent log console feed (keyset, newest-first).
 *
 * L3: with no phone selected the query is DISABLED — it never hits the
 * network. Mirrors usePhoneDossier's caller-gate AND intrinsic-gate pattern.
 */
export function useAgentLogs(
  phoneId: string | null,
  filter: AgentLogsFilter = {},
  opts: AgentLogsOptions = {},
) {
  return useInfiniteQuery({
    queryKey: ['agent-logs', phoneId, filter],
    enabled: (opts.enabled ?? true) && !!phoneId,
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) => {
      const params = new URLSearchParams()
      params.set('phone_id', phoneId as string)
      if (pageParam) params.set('cursor', pageParam)
      if (filter.level) params.set('level', filter.level)
      if (filter.since) params.set('since', filter.since)
      params.set('limit', String(filter.limit ?? 200))
      return api.get<AgentLogsPage>(`/api/v1/admin/agent-logs?${params.toString()}`)
    },
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
  })
}

/**
 * Poll ONLY the newest page (no cursor) for live mode.
 *
 * Separate from `useAgentLogs` so the live tick fetches a single page instead of
 * re-fetching every loaded keyset page (an infinite query's refetchInterval
 * refetches all pages, work scaling with scroll depth). New rows only ever land
 * on page 0; the caller merges these into the paged history by id (see
 * mergeChronological).
 */
export function useAgentLogsLatest(
  phoneId: string | null,
  filter: AgentLogsFilter = {},
  opts: AgentLogsOptions = {},
) {
  return useQuery({
    queryKey: ['agent-logs-latest', phoneId, filter],
    enabled: (opts.enabled ?? true) && !!phoneId,
    queryFn: () => {
      const params = new URLSearchParams()
      params.set('phone_id', phoneId as string)
      if (filter.level) params.set('level', filter.level)
      if (filter.since) params.set('since', filter.since)
      params.set('limit', String(filter.limit ?? 200))
      return api.get<AgentLogsPage>(`/api/v1/admin/agent-logs?${params.toString()}`)
    },
    refetchInterval: opts.refetchInterval ?? false,
  })
}

/** Current global agent PIN + audit metadata (admin-only). */
export function useAgentPin() {
  return useQuery({
    queryKey: ['agent-pin'],
    queryFn: () => api.get<AgentPin>('/api/v1/admin/settings/agent-pin'),
  })
}

/** Set (string) or clear (null) the global agent PIN. */
export function useUpdateAgentPin() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (pin: string | null) =>
      api.put<AgentPin>('/api/v1/admin/settings/agent-pin', { pin }),
    onSuccess: (data) => qc.setQueryData(['agent-pin'], data),
  })
}
